import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Grid, FormHelperText } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { UserContext } from "App";
import FullScreenLoader from "components/Custom/FullScreenLoader";
import { AddNewEventAPI, UploadEventMediaAPI } from "APIs/EventAPIs";
import EventSchedules from "./schedules";
import {
  EventFormHeader,
  EventFormActions,
  EventDetailsFields,
  MediaPicker,
  MediaGrid,
} from "./fromHeader";
import { useEventForm, useNewFiles } from "./useEventForm";
import { buildEventPayload, buildMediaFormData } from "./eventFromUtils";
import { notify } from "App";
// Receipt page theme (shared)
import SectionCard, { useAccent } from "layouts/receipt/components/SectionCard";
import FormField from "layouts/receipt/components/FormField";

// Event pages: smaller inner padding for the cards, so the content gets more width.
// (only affects the cards inside this wrapper, the shared SectionCard is not changed)
const compactCardsSx = { "& > div": { padding: { xs: "12px", md: "16px" } } };

// The Save button lives outside the <form>, so it points to the form by this id
const FORM_ID = "add-event-form";

const IMAGE_REQUIRED_MSG = "At least one image is required.";

const AddEvent = () => {
  const userData = useContext(UserContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accentName } = useAccent();

  const {
    eventData,
    scheduleData,
    handleEventChange,
    handleEventBlur,
    handleScheduleChange,
    handleScheduleBlur,
    addSchedule,
    deleteSchedule,
    reorderSchedule,
    getVisibleEventError,
    getVisibleScheduleError,
    isFormValid,
    touchAllFields,
  } = useEventForm({ blankSchedule: true });
  const { newFiles, addFiles, removeFile } = useNewFiles();
  const [loading, setLoading] = useState(false);

  // Image is compulsory: error is shown in red under the upload input
  const [imageError, setImageError] = useState("");

  const goToList = () => navigate("/event");

  // Add the files and clear the error
  const handleAddFiles = (...args) => {
    addFiles(...args);
    setImageError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const hasNoImage = newFiles.length === 0;

    // Invalid form or no image: show every error (missing fields + image) at once
    if (!isFormValid || hasNoImage) {
      if (hasNoImage) setImageError(IMAGE_REQUIRED_MSG);
      if (!isFormValid) touchAllFields();
      return;
    }

    setLoading(true);
    try {
      const payload = buildEventPayload(eventData, scheduleData);
      const created = await AddNewEventAPI(payload, userData.access_token, dispatch);
      if (created === null) return;

      const formData = buildMediaFormData(created.events_id, newFiles);
      const uploaded = await UploadEventMediaAPI(formData, userData.access_token, dispatch);
      if (uploaded === null) return;

      notify("Event added!", "success");
      goToList();
    } finally {
      setLoading(false);
    }
  };

  const newImageItems = newFiles.map((item) => ({
    key: item.id,
    src: item.preview,
    href: item.preview,
    alt: "new_preview",
    onDelete: () => removeFile(item.id),
  }));

  return (
    <MDBox px={{ xs: 0, md: 1 }} pt={{ xs: 0, md: 1 }} pb={3}>
      <FullScreenLoader open={loading} />

      {/* Back button sits right beside the page badge */}
      <EventFormHeader icon="event" label="ADD EVENT" onBack={goToList} />

      <form id={FORM_ID} onSubmit={handleSubmit} noValidate autoComplete="off">
        <MDBox sx={compactCardsSx}>
          <SectionCard title="Event" subtitle="Fields marked * are required">
            <EventDetailsFields
              eventData={eventData}
              onChange={handleEventChange}
              onBlur={handleEventBlur}
              getVisibleError={getVisibleEventError}
            />
          </SectionCard>

          <EventSchedules
            data={scheduleData}
            onAdd={addSchedule}
            onChange={handleScheduleChange}
            onBlur={handleScheduleBlur}
            onDelete={deleteSchedule}
            onReorder={reorderSchedule}
            getVisibleError={getVisibleScheduleError}
            eventStartDate={eventData.event_start_date}
            eventEndDate={eventData.event_end_date}
          />

          <SectionCard title="Media" subtitle="At least one image is required">
            <Grid container spacing={2}>
              {/* Image Previews Section */}
              {newImageItems.length > 0 && (
                <Grid item xs={12}>
                  <MDTypography
                    variant="button"
                    fontWeight="bold"
                    color={accentName}
                    display="block"
                    mb={1.5}
                  >
                    Selected Images ({newImageItems.length})
                  </MDTypography>
                  <MediaGrid items={newImageItems} isNew />
                </Grid>
              )}

              {/* Media Upload Input */}
              <FormField name="upload_media" required full>
                <MediaPicker onChange={handleAddFiles} />
                {imageError && <FormHelperText error>{imageError}</FormHelperText>}
              </FormField>
            </Grid>
          </SectionCard>
        </MDBox>
      </form>

      {/* Cancel + Save sit outside the form: bottom right on desktop, centered on mobile.
          Save is always enabled (only disabled while saving). */}
      <EventFormActions
        formId={FORM_ID}
        onCancel={goToList}
        submitLabel="Save Event"
        submitDisabled={loading}
      />
    </MDBox>
  );
};

export default AddEvent;
